import { useTranslation } from 'react-i18next';
import { ResourceCrudPage, type ResourceListArgs, type PaginatedResult } from '@vipso/shared';
import {
  useListWarehousesQuery,
  useAddUpdateWarehouseMutation,
  useDeleteWarehouseMutation,
} from '../api/enhanceGenerated';
import { useAccess } from '../hooks/useAccess';

type WarehouseRow = { id: string; name: string; phone?: string; email?: string };

function useWarehouseList(args: ResourceListArgs) {
  const result = useListWarehousesQuery(args);
  const data: PaginatedResult<WarehouseRow> | undefined = result.data?.listWarehouses
    ? {
        ...result.data.listWarehouses,
        rows: result.data.listWarehouses.rows.map((r) => ({
          id: r.id,
          name: r.name,
          phone: r.phone ?? undefined,
          email: r.email ?? undefined,
        })),
      }
    : undefined;
  return { ...result, data };
}

export function WarehousePage() {
  const { t } = useTranslation();
  const { canMutate } = useAccess();
  const [save, { isLoading: saving }] = useAddUpdateWarehouseMutation();
  const [remove] = useDeleteWarehouseMutation();

  return (
    <ResourceCrudPage<WarehouseRow>
      title={t('nav.warehouse')}
      description={t('warehouse.description')}
      entityLabel={t('warehouse.entity')}
      canMutate={canMutate()}
      saving={saving}
      useList={useWarehouseList}
      fields={[
        { key: 'name', label: t('common.name'), required: true },
        { key: 'phone', label: t('common.phone') },
        { key: 'email', label: t('common.email') },
      ]}
      onSave={(values, editing) =>
        save({ input: { ...values, id: editing?.id } }).unwrap()
      }
      onDelete={(row) => remove({ id: row.id }).unwrap()}
    />
  );
}
